import { FunctionComponent, useState } from 'react';
import { useRouter } from 'next/router';

import { Routes } from 'types/routes';

import { RegisterTypes } from 'models/User';
import UserAgree, { Types } from 'models/UserAgree';

import Button, { ButtonTypes } from 'components/layout/Button';
import Checkbox from 'components/layout/forms/Checkbox';
import Form from 'components/layout/forms/Form';
import Input from 'components/layout/forms/Input';
import InputRow from 'components/layout/forms/InputRow';
import PasswordValidation, { Validation } from 'components/layout/forms/PasswordValidation';
import ReCAPTCHA from 'components/layout/ReCAPTCHA';

import { Errors, Props } from './index';
import StyledComponent from './styles';

const PublicLayoutFormsEmployeeRegisterForm: FunctionComponent<Props> = ({
    headline,
    input,
    buttonProps,
    onSuccess,
    actions,
    phoneForm,
    emailForm,
    helper,
}) => {
    const router = useRouter();

    const [isPasswordVisible, setIsPasswordVisible]: [boolean, Function] = useState(false);
    const [errorMessage, setErrorMessage]: [Errors, Function] = useState(null);
    const [recaptchaToken, setRecaptchaToken]: [string, Function] = useState(null);
    const [isPasswordValid, setIsPasswordValid]: [boolean, Function] = useState(false);

    const handlePasswordChange = (passwordTests: Validation) => {
        if (!passwordTests) {
            return;
        }
        const notPassed = Object.values(passwordTests).filter(passwordTest => {
            return passwordTest === false;
        });
        if (notPassed.length > 0) {
            setIsPasswordValid(false);
        } else {
            setIsPasswordValid(true);
        }
    };

    return (
        <StyledComponent className="public-layout-forms-employee-register-form">
            <Form
                initialValues={{
                    phonePrefix: input?.phonePrefix?.initialValue,
                }}
                submitAction={async (values) => {
                    const getUserAgrees = (): UserAgree[] => {
                        let userAgrees = [];

                        if (phoneForm) {
                            userAgrees = [{
                                type: Types.PhoneMarketing,
                                content: input?.phoneMarketing,
                                value: values?.phoneMarketing || false,
                            }, {
                                type: Types.PhoneMarketingClients,
                                content: input?.phoneMarketingClients,
                                value: values?.phoneMarketingClients || false,
                            }];
                        }

                        if (emailForm) {
                            userAgrees = [{
                                type: Types.EmailMarketing,
                                content: input?.emailMarketing,
                                value: values?.emailMarketing || false,
                            }, {
                                type: Types.EmailMarketingClients,
                                content: input?.emailMarketingClients,
                                value: values?.emailMarketingClients || false,
                            }];
                        }

                        return userAgrees;
                    };
                    try {
                        await actions.register({
                            email: values?.email && values?.email,
                            password: values?.password,
                            phone: values?.phone && values?.phone,
                            phonePrefix: values?.phonePrefix && values?.phonePrefix,
                            firstName: values?.firstName,
                            lastName: values?.lastName,
                            registerType: phoneForm ? RegisterTypes.Phone : RegisterTypes.Email,
                            userAgrees: [
                                ...getUserAgrees(),
                                {
                                    type: Types.Regulations,
                                    content: input?.regulations,
                                    value: values?.regulations || false,
                                }],
                            recaptchaToken,
                        })
                            .then(() => router.push(Routes.PublicActivationAccount));
                    } catch (error) {
                        console.error(error);
                        setErrorMessage(error?.payload?.originalError?.data?.errors);
                        throw error;
                    }
                }}
                onSuccess={onSuccess}
            >
                {({ formik }) => {
                    return (
                        <div className="cols">
                            <div className="col-left">
                                <div className="section">

                                    <div
                                        className="headline"
                                        dangerouslySetInnerHTML={{ __html: headline?.register }}
                                    />
                                    <div className="control">
                                        <div className="button-mobile-wrapper">
                                            <div className="mobile-links">
                                                <div className="links">
                                                    <a
                                                        href={Routes.PublicLoginWorker}
                                                        className="links-page login-page"
                                                    >
                                                        Zaloguj się
                                                    </a>
                                                    <span
                                                        className="links-page link-register"
                                                    >
                                                            Zarejestruj się
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {phoneForm && (
                                        <InputRow className="phone-container">
                                            <div className="phone">
                                                <Input
                                                    name="phonePrefix"
                                                    placeholder={input?.phonePrefix?.placeholder}
                                                    onChange={({ target: { value } }) => formik.values?.phonePrefix?.length >= 3
                                                        ? formik.setFieldValue('phonePrefix', formik?.values?.phonePrefix?.slice(0, 2))
                                                        : formik.setFieldValue('phonePrefix', value)
                                                    }
                                                    value={formik.values.phonePrefix}
                                                    error={errorMessage?.phonePrefix}
                                                />
                                                <Input
                                                    name="phone"
                                                    type="number"
                                                    label={input?.phone?.label}
                                                    placeholder={input?.phone?.label}
                                                    onChange={formik.handleChange}
                                                    value={formik.values.phone}
                                                    error={errorMessage?.phone}
                                                />
                                            </div>
                                        </InputRow>
                                    )}

                                    {emailForm && (
                                        <InputRow>
                                            <Input
                                                name="email"
                                                type="text"
                                                label={input?.email?.label}
                                                placeholder={input?.email?.placeholder}
                                                onChange={formik.handleChange}
                                                value={formik.values.email}
                                                error={errorMessage?.email}
                                            />
                                        </InputRow>
                                    )}

                                    <InputRow>
                                        <Input
                                            name="password"
                                            type={isPasswordVisible ? 'text' : 'password'}
                                            label={input?.password?.label}
                                            placeholder={input?.password?.placeholder}
                                            onChange={formik.handleChange}
                                            value={formik.values.password}

                                            decoratorRight={{
                                                children: (
                                                    <a
                                                        onClick={() => {
                                                            setIsPasswordVisible(!isPasswordVisible);
                                                        }}
                                                    >
                                                        {input?.password?.title}
                                                    </a>
                                                ),
                                            }}
                                            error={errorMessage?.password}
                                        />

                                    </InputRow>
                                    <InputRow>
                                        <PasswordValidation
                                            translations={input?.password.validation}
                                            password={formik.values.password}
                                            checkIfValid={handlePasswordChange}
                                        />

                                    </InputRow>
                                </div>
                            </div>
                            <div className="col-right">
                                <div className="section">
                                    <div
                                        className="headline"
                                        dangerouslySetInnerHTML={{ __html: headline?.data }}
                                    />
                                    <InputRow>
                                        <Input
                                            name="firstName"
                                            type="text"
                                            label={input?.firstName?.label}
                                            placeholder={input?.firstName?.placeholder}
                                            onChange={formik.handleChange}
                                            value={formik.values.firstName}
                                            error={errorMessage?.firstName}
                                        />
                                        <Input
                                            name="lastName"
                                            type="text"
                                            label={input?.lastName?.label}
                                            placeholder={input?.lastName?.placeholder}
                                            onChange={formik.handleChange}
                                            value={formik.values.lastName}
                                            error={errorMessage?.lastName}
                                        />
                                    </InputRow>
                                    <InputRow>
                                        <Checkbox
                                            name="regulations"
                                            label={input?.regulations}
                                            onChange={formik.handleChange}
                                            value={formik.values.regulations}
                                            error={errorMessage?.userAgrees}
                                            expandable
                                        />
                                    </InputRow>
                                    {phoneForm && (
                                        <>
                                            <InputRow>
                                                <Checkbox
                                                    name="phoneMarketing"
                                                    label={input?.phoneMarketing}
                                                    onChange={formik.handleChange}
                                                    value={formik.values.phoneMarketing}
                                                    expandable
                                                />
                                            </InputRow>
                                            <InputRow>
                                                <Checkbox
                                                    name="phoneMarketingClients"
                                                    label={input?.phoneMarketingClients}
                                                    onChange={formik.handleChange}
                                                    value={formik.values.phoneMarketingClients}
                                                    expandable
                                                />
                                            </InputRow>
                                        </>
                                    )}
                                    {emailForm && (
                                        <>
                                            <InputRow>
                                                <Checkbox
                                                    name="emailMarketing"
                                                    label={input?.emailMarketing}
                                                    onChange={formik.handleChange}
                                                    value={formik.values.emailMarketing}
                                                    expandable
                                                />
                                            </InputRow>
                                            <InputRow>
                                                <Checkbox
                                                    name="emailMarketingClients"
                                                    label={input?.emailMarketingClients}
                                                    onChange={formik.handleChange}
                                                    value={formik.values.emailMarketingClients}
                                                    expandable
                                                />
                                            </InputRow>
                                        </>
                                    )}
                                </div>

                                <div className="google-recaptcha">
                                    <ReCAPTCHA
                                        onChange={(token: string) => setRecaptchaToken(token)}
                                        error={errorMessage?.recaptchaToken}
                                    />
                                </div>

                                <div className="btn-desktop">
                                    <Button
                                        {...buttonProps}
                                        loading={formik.isSubmitting}
                                        type={ButtonTypes.Submit}
                                        disabled={!isPasswordValid}
                                    />
                                    <div
                                        className="helper"
                                        dangerouslySetInnerHTML={{ __html: helper }}
                                    />
                                </div>
                            </div>
                        </div>
                    );
                }}
            </Form>
        </StyledComponent>
    );
};

export default PublicLayoutFormsEmployeeRegisterForm;